import { Control, FieldValues, Path, useController } from 'react-hook-form'
import React, { useEffect, useMemo } from 'react'
import { ErrorLabel } from '../ErrorLabel'
import lodash from 'lodash'
import { ReactCreatableSelectWrapper } from '../ReactCreatableSelectWrapper'
import { cn } from '../../UI/cn'

export interface SelectOption {
  label: string
  value: unknown
}

export interface ICreatableSelectProps<T extends FieldValues, U> {
  control: Control<T, any>
  name: Path<T>
  label?: string | null
  placeholder?: string
  type?: string
  options?: U[]
  isMulti?: boolean
  disableFiltering?: boolean
  isLoading?: boolean
  allowCreateWhileLoading?: boolean
  onCreateOption: (inputValue: string) => Promise<U | null>
  onSearchChange?: (search: string) => void
  disableCreate?: boolean
  /**
   * Predicate controlling whether the "Create 'X'" option appears in the
   * menu. Defaults to `input.length > 0`. Pass a stricter check to block
   * creation of invalid values (e.g. non-email strings in recipient fields).
   */
  isValidNewOption?: (input: string) => boolean
  style?: React.CSSProperties
  initialSearch?: string
  menuPosition?: 'fixed' | 'absolute'
  clearSearchOnSelect?: boolean
  disabled?: boolean
  readOnly?: boolean
  className?: string
  optional?: boolean
  height?: string
  isClearable?: boolean
  styles?: any
  labelClassName?: string
  /** Custom renderer for option labels. Context is 'menu' (dropdown) or 'value' (selected tag). */
  formatOptionLabel?: (
    option: U,
    meta: { context: 'menu' | 'value' }
  ) => React.ReactNode
  /** Override the menu message shown when there are no options. Receives
   *  the current input value so callers can render different copy for
   *  "haven't typed yet" vs "no matches found". Falls through to
   *  react-select's default ("No options") when omitted. */
  noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode
}

export default function CreatableSelect<
  T extends FieldValues,
  U extends FieldValues,
>(props: ICreatableSelectProps<T, U>) {
  const [internalIsLoading, setInternalIsLoading] = React.useState(false)
  const [createdOptions, setCreatedOptions] = React.useState<U[]>([])
  const [search, setSearch] = React.useState(props.initialSearch || '')

  const allOptions = useMemo(
    () => props.options?.concat(createdOptions),
    [props.options, createdOptions]
  )

  const {
    field: { ref, onChange, value },
    fieldState: { error },
  } = useController({ name: props.name, control: props.control })

  const compareObjects = (o1: any, o2: any) => {
    return lodash.isEqual(o1, o2)
  }

  // Synthesize a chip-shaped option from a raw form value when it
  // doesn't appear in the current ``allOptions`` list. Without this,
  // previously-selected entries silently disappear from the rendered
  // chips whenever the options array narrows — e.g. the email
  // recipient picker and CompanyAutocomplete are both search-driven,
  // so typing a new query trims the API result set and any chip whose
  // option came from the prior result drops out of ``allOptions``.
  // The form value is unchanged (submit still includes it), but the
  // user can't see the selection.
  //
  // Strategy: string values use the value itself as label; object
  // values fall back to ``label`` / ``name`` properties (the two
  // shapes used across consumers — CompanyAutocomplete writes
  // ``{id, name, domain}``, others use ``{label, value}`` directly).
  // Anything else returns null and is filtered, preserving existing
  // behaviour for shapes we don't recognize.
  const fallbackOptionForValue = (v: unknown): any => {
    if (typeof v === 'string') return { label: v, value: v }
    if (typeof v === 'object' && v != null) {
      const obj = v as Record<string, unknown>
      const label =
        (typeof obj.label === 'string' && obj.label) ||
        (typeof obj.name === 'string' && obj.name) ||
        null
      if (label) return { label, value: v }
    }
    return null
  }

  const _value = useMemo(() => {
    if (!value) return null
    if (props.isMulti) {
      return (value as any[])
        .map(
          (v) =>
            allOptions?.find((option: any) =>
              compareObjects(option.value, v)
            ) ?? fallbackOptionForValue(v)
        )
        .filter(Boolean)
    } else {
      return (
        allOptions?.find((option: any) =>
          compareObjects(option.value, value)
        ) ?? fallbackOptionForValue(value)
      )
    }
  }, [value, allOptions, props.isMulti])

  useEffect(() => {
    if (props.clearSearchOnSelect && _value) {
      setSearch('')
    }
  }, [_value])

  const handleCreate = (inputValue: string) => {
    setInternalIsLoading(true)
    props
      .onCreateOption(inputValue)
      .then((option) => {
        if (!option) return

        setCreatedOptions([...createdOptions, option])
        if (props.isMulti) {
          onChange([...value, option.value])
        } else {
          onChange(option.value)
        }
      })
      .finally(() => {
        setInternalIsLoading(false)
      })
  }

  const handleInputChange = (inputValue: string) => {
    if (props.readOnly) return
    setSearch(inputValue)
    props.onSearchChange?.(inputValue)
  }

  return (
    <div className={cn('mt-3', props.className)} style={props.style}>
      {props.label && (
        <label
          className={cn(
            'text-base font-medium text-gray-800 select-none mb-1',
            props.labelClassName
          )}
          htmlFor={'select-' + props.name.toString()}
        >
          {props.label}
          {props.optional && (
            <span className={'text-sm italic font-normal'}> - optional</span>
          )}
        </label>
      )}

      <ReactCreatableSelectWrapper
        onChange={(newValue: any) => {
          if (props.isMulti) {
            onChange(newValue.map((v: any) => v.value))
          } else {
            onChange(newValue?.value)
          }
          if (!newValue) {
            onChange(props.isMulti ? [] : null)
          }
        }}
        isClearable={props.isClearable !== undefined ? props.isClearable : true}
        name={props.name}
        id={'select-' + props.name}
        options={allOptions}
        value={_value}
        ref={ref}
        isMulti={props.isMulti}
        isDisabled={props.disabled || internalIsLoading}
        isLoading={internalIsLoading || props.isLoading}
        allowCreateWhileLoading={props.allowCreateWhileLoading}
        onCreateOption={handleCreate}
        onInputChange={handleInputChange}
        isValidNewOption={(inputValue: any) => {
          if (props.disableCreate) return false
          if (props.isValidNewOption) return props.isValidNewOption(inputValue)
          return inputValue.length > 0
        }}
        inputValue={search}
        placeholder={props.placeholder}
        filterOption={props.disableFiltering ? () => true : undefined}
        height={props.height}
        styles={props.styles}
        formatOptionLabel={props.formatOptionLabel}
        noOptionsMessage={props.noOptionsMessage}
      />

      {error?.message && <ErrorLabel message={error?.message} />}
    </div>
  )
}
